import { useState, useMemo } from "react";
import PassifEleve from "./PassifEleve";
import AcquisitionEleve from "./AcquisitionEleve";
import { useClasseContext } from "../../context/ClasseContext";
import { useUser } from "../../context/UserContext";
import { isInPeriode as isDansPeriode } from "../../utils/periode";
import { filterResultatsRole } from "../../utils/filtrageResultats";
import { niveauDeLaClasse } from "../CollerNotes/PastilleNiveau";

function DetailEleve({selectedEleveId, resultats, handleRowClick, getClassementEleve, calculerMoyenne, getNbEval, listEleves}) {
  const {ActivePeriode} = useClasseContext()
  const [typeView, setTypeView] = useState('acquisition');
  const [viewLevel, setViewLevel] = useState('savoir');
  const [searchTerm, setSearchTerm] = useState('');
  const {classeNom, groupNom} = useClasseContext();
  const {user, matiereFilter, resultatsFilter} = useUser();
  
  // Récupérer l'élève depuis listEleves au lieu de l'API
  const eleve = useMemo(() => {
    if (!listEleves || !selectedEleveId) return null;
    return listEleves.find(e => e.id === selectedEleveId);
  }, [listEleves, selectedEleveId]);

  const filteredEleve = useMemo(() => {
    // Toujours reboucler même si pas de résultats
    if (!eleve?.resultats) {
      return { ...eleve, resultats: [] };
    }
    return {
      ...eleve,
      resultats: filterResultatsRole(eleve.resultats, {
        role: user.role,
        matiereFilter,
        groupNom,
        resultatsFilter,
      }),
    };
  }, [eleve, user.role, matiereFilter, groupNom, resultatsFilter]);

  // Helper : calcul de position dans la classe (percentile + écart-type)
  const computePosition = (moyenneEleve, classeMoyennes) => {
    if (!classeMoyennes || classeMoyennes.length === 0) {
      return { positionClasse: "Non évalué", positionColor: "text-gray-500", moyenneClasse: null };
    }
    const moyennesTriees = [...classeMoyennes].sort((a, b) => b - a);
    const moyenneClasse = moyennesTriees.reduce((sum, m) => sum + m, 0) / moyennesTriees.length;
    const ecartType = Math.sqrt(
      moyennesTriees.reduce((sum, m) => sum + Math.pow(m - moyenneClasse, 2), 0) / moyennesTriees.length
    );
    const position = moyennesTriees.findIndex(m => moyenneEleve >= m);
    const percentile = position === -1 ? 100 : (position / moyennesTriees.length) * 100;

    let positionClasse, positionColor;
    if (percentile <= 20) {
      positionClasse = "Parmi les meilleurs de la classe";
      positionColor = "text-green-800";
    } else if (moyenneEleve > moyenneClasse + ecartType * 0.5) {
      positionClasse = "Au-dessus de la moyenne de classe";
      positionColor = "text-teal-500";
    } else if (moyenneEleve >= moyenneClasse - ecartType * 0.5) {
      positionClasse = "Dans la moyenne de la classe";
      positionColor = "text-blue-500";
    } else {
      positionClasse = "En-dessous de la moyenne de la classe";
      positionColor = "text-orange-500";
    }
    return { positionClasse, positionColor, moyenneClasse };
  };

  // Calcul des résultats à 3 niveaux : savoir, descripteur, thème
  const { savoirEtudiés, descripteurEtudiés, themeEtudiés } = useMemo(() => {
    // Filtre par période active (helper partagé, lié à ActivePeriode)
    const isInPeriode = (evalDate) => isDansPeriode(evalDate, ActivePeriode);

    const savoirsClasse = {};
    const descripteursClasse = {};
    const themesClasse = {};

    // ── Pass 1 : Toute la classe (pour comparaison de position) ──
    if (listEleves && listEleves.length > 0) {
      listEleves.forEach(eleve => {
        const eleveParSavoir = {};

        eleve.resultats?.forEach(resultat => {
          if (!isInPeriode(resultat.evalDate)) return;

          const { savoirFaireId, attenduId, scores, descripteurId, themeNom } = resultat;
          const key = `${savoirFaireId ? 'savoir' : 'attendu'}-${savoirFaireId || attenduId}`;

          if (!eleveParSavoir[key]) {
            eleveParSavoir[key] = { total: 0, count: 0, descripteurId, themeNom };
          }
          if (scores) {
            eleveParSavoir[key].total += scores;
            eleveParSavoir[key].count += 1;
          }
        });

        // Agréger savoir → descripteur → thème pour cet élève
        const eleveParDescripteur = {};
        Object.entries(eleveParSavoir).forEach(([key, data]) => {
          if (data.count > 0) {
            const moyenne = data.total / data.count;

            // Niveau savoir
            if (!savoirsClasse[key]) savoirsClasse[key] = { moyennes: [] };
            savoirsClasse[key].moyennes.push(moyenne);

            // Niveau descripteur
            if (data.descripteurId) {
              const descKey = `desc-${data.descripteurId}`;
              if (!eleveParDescripteur[descKey]) {
                eleveParDescripteur[descKey] = { total: 0, count: 0, themeNom: data.themeNom };
              }
              eleveParDescripteur[descKey].total += moyenne;
              eleveParDescripteur[descKey].count += 1;
            }
          }
        });

        const eleveParTheme = {};
        Object.entries(eleveParDescripteur).forEach(([descKey, data]) => {
          if (data.count > 0) {
            const moyenneDesc = data.total / data.count;

            if (!descripteursClasse[descKey]) descripteursClasse[descKey] = { moyennes: [] };
            descripteursClasse[descKey].moyennes.push(moyenneDesc);

            if (!eleveParTheme[data.themeNom]) {
              eleveParTheme[data.themeNom] = { total: 0, count: 0 };
            }
            eleveParTheme[data.themeNom].total += moyenneDesc;
            eleveParTheme[data.themeNom].count += 1;
          }
        });

        Object.entries(eleveParTheme).forEach(([themeNom, data]) => {
          if (data.count > 0) {
            if (!themesClasse[themeNom]) themesClasse[themeNom] = { moyennes: [] };
            themesClasse[themeNom].moyennes.push(data.total / data.count);
          }
        });
      });
    }

    // ── Pass 2 : Élève sélectionné ──
    if (!filteredEleve) return { savoirEtudiés: [], descripteurEtudiés: [], themeEtudiés: [] };

    const savoirs = {};

    filteredEleve.resultats?.forEach(resultat => {
      if (!isInPeriode(resultat.evalDate)) return;

      const { savoirFaireId, savoirTitre, attenduId, attenduTitre, scores, themeNom, descripteurId, descripteurNom, evalDate, attenduNiveau } = resultat;
      const id = savoirFaireId || attenduId;
      const titre = savoirTitre || attenduTitre;
      const type = savoirFaireId ? 'savoir' : 'attendu';
      const uniqueKey = `${type}-${id}`;

      if (!savoirs[uniqueKey]) {
        savoirs[uniqueKey] = {
          totalScores: 0, count: 0,
          titre, theme: themeNom, descripteurId, descripteurNom,
          derniereEvalDate: null,
          niveaux: []
        };
      }
      // Niveau de l'attendu (2026-09-29) : sert SEULEMENT à la pastille « 6e »
      // d'une carte hors du niveau de la classe ; aucune moyenne ni position
      // n'en dépend.
      if (attenduNiveau && !savoirs[uniqueKey].niveaux.includes(attenduNiveau)) {
        savoirs[uniqueKey].niveaux.push(attenduNiveau);
      }

      if (scores) {
        savoirs[uniqueKey].totalScores += scores;
        savoirs[uniqueKey].count += 1;
        if (evalDate) {
          const currentDate = new Date(evalDate);
          if (!savoirs[uniqueKey].derniereEvalDate || currentDate > new Date(savoirs[uniqueKey].derniereEvalDate)) {
            savoirs[uniqueKey].derniereEvalDate = evalDate;
          }
        }
      }
    });

    // Niveau savoir/attendu
    const savoirList = Object.entries(savoirs).map(([uniqueKey, savoir]) => {
      const moyenneEleve = savoir.count > 0 ? (savoir.totalScores / savoir.count) : 0;
      const pos = savoir.count > 0
        ? computePosition(moyenneEleve, savoirsClasse[uniqueKey]?.moyennes)
        : { positionClasse: "Non évalué", positionColor: "text-gray-500", moyenneClasse: null };

      return {
        titre: savoir.titre,
        theme: savoir.theme,
        descripteurId: savoir.descripteurId,
        descripteurNom: savoir.descripteurNom,
        niveaux: savoir.niveaux,
        moyenne: moyenneEleve,
        count: savoir.count,
        derniereEvaluation: savoir.derniereEvalDate,
        ...pos
      };
    });

    // Niveau descripteur : agréger les savoirs par descripteurId
    const descMap = {};
    savoirList.forEach(s => {
      if (s.moyenne === 0 || !s.descripteurId) return;
      const key = `desc-${s.descripteurId}`;
      if (!descMap[key]) {
        descMap[key] = {
          descripteurId: s.descripteurId,
          titre: s.descripteurNom,
          theme: s.theme,
          totalMoyennes: 0, countSavoirs: 0,
          derniereEvalDate: null,
          savoirs: [],
          niveaux: []
        };
      }
      s.niveaux.forEach((n) => { if (!descMap[key].niveaux.includes(n)) descMap[key].niveaux.push(n); });
      descMap[key].totalMoyennes += s.moyenne;
      descMap[key].countSavoirs += 1;
      descMap[key].savoirs.push({ titre: s.titre, moyenne: s.moyenne });
      if (s.derniereEvaluation) {
        const d = new Date(s.derniereEvaluation);
        if (!descMap[key].derniereEvalDate || d > new Date(descMap[key].derniereEvalDate)) {
          descMap[key].derniereEvalDate = s.derniereEvaluation;
        }
      }
    });

    const descripteurList = Object.entries(descMap).map(([descKey, desc]) => {
      const moyenneEleve = desc.totalMoyennes / desc.countSavoirs;
      const pos = computePosition(moyenneEleve, descripteursClasse[descKey]?.moyennes);
      const sorted = [...desc.savoirs].sort((a, b) => b.moyenne - a.moyenne);
      const best = sorted.length > 0 ? sorted[0] : null;
      const worst = sorted.length > 1 ? sorted[sorted.length - 1] : null;
      return {
        titre: desc.titre,
        theme: desc.theme,
        niveaux: desc.niveaux,
        moyenne: moyenneEleve,
        count: desc.countSavoirs,
        derniereEvaluation: desc.derniereEvalDate,
        best, worst,
        ...pos
      };
    });

    // Niveau thème : agréger les descripteurs par themeNom
    const themeMap = {};
    descripteurList.forEach(d => {
      if (!themeMap[d.theme]) {
        themeMap[d.theme] = {
          titre: d.theme,
          theme: d.theme,
          totalMoyennes: 0, countDescs: 0,
          savoirCount: 0,
          derniereEvalDate: null,
          descripteurs: [],
          niveaux: []
        };
      }
      d.niveaux.forEach((n) => { if (!themeMap[d.theme].niveaux.includes(n)) themeMap[d.theme].niveaux.push(n); });
      themeMap[d.theme].totalMoyennes += d.moyenne;
      themeMap[d.theme].countDescs += 1;
      themeMap[d.theme].savoirCount += d.count;
      themeMap[d.theme].descripteurs.push({ titre: d.titre, moyenne: d.moyenne });
      if (d.derniereEvaluation) {
        const dt = new Date(d.derniereEvaluation);
        if (!themeMap[d.theme].derniereEvalDate || dt > new Date(themeMap[d.theme].derniereEvalDate)) {
          themeMap[d.theme].derniereEvalDate = d.derniereEvaluation;
        }
      }
    });

    const themeList = Object.entries(themeMap).map(([themeNom, t]) => {
      const moyenneEleve = t.totalMoyennes / t.countDescs;
      const pos = computePosition(moyenneEleve, themesClasse[themeNom]?.moyennes);
      const sorted = [...t.descripteurs].sort((a, b) => b.moyenne - a.moyenne);
      const best = sorted.length > 0 ? sorted[0] : null;
      const worst = sorted.length > 1 ? sorted[sorted.length - 1] : null;
      return {
        titre: t.titre,
        theme: t.theme,
        niveaux: t.niveaux,
        moyenne: moyenneEleve,
        count: t.countDescs,
        descCount: t.countDescs,
        savoirCount: t.savoirCount,
        derniereEvaluation: t.derniereEvalDate,
        best, worst,
        ...pos
      };
    });

    return { savoirEtudiés: savoirList, descripteurEtudiés: descripteurList, themeEtudiés: themeList };
  }, [filteredEleve, ActivePeriode, listEleves]);

  // Calculer les statistiques de l'élève
  const eleveStats = useMemo(() => {
    if (!filteredEleve) return null;
    
    const stats = {
      moyenne: calculerMoyenne ? calculerMoyenne(filteredEleve) : 0,
      rang: getClassementEleve ? getClassementEleve(filteredEleve, listEleves) && getClassementEleve(filteredEleve, listEleves).rang : 0,
      totalEleves: listEleves ? listEleves.length : 0,
      evaluationsPassees: getNbEval ? getNbEval(filteredEleve) : 0,
      tauxReussite: 0,
      progression: 0
    };

    // Calculer le taux de réussite (scores >= 4)
    if (filteredEleve.resultats && filteredEleve.resultats.length > 0) {
      // Filtrer uniquement les résultats avec des scores valides (ni 0, ni undefined, ni null)
      const resultatsAvecScores = filteredEleve.resultats.filter(r => 
        r.scores !== undefined && r.scores !== null && r.scores !== 0
      );
      
      if (resultatsAvecScores.length > 0) {
        const reussites = resultatsAvecScores.filter(r => r.scores >= 5).length;
        stats.tauxReussite = Math.round((reussites / resultatsAvecScores.length) * 100);
      }
    }

    return stats;
  }, [filteredEleve, calculerMoyenne, getClassementEleve, getNbEval, listEleves]);

  const handleTypeViewChange = (item) => {
    setTypeView(item);
  }

  // Sélectionner les données selon le niveau de vue, puis filtrer
  const filteredItems = useMemo(() => {
    const currentData = viewLevel === 'theme' ? themeEtudiés
                      : viewLevel === 'descripteur' ? descripteurEtudiés
                      : savoirEtudiés;

    if (!searchTerm) return currentData;
    const term = searchTerm.toLowerCase();
    return currentData.filter(item =>
      item.titre?.toLowerCase().includes(term) ||
      item.theme?.toLowerCase().includes(term)
    );
  }, [searchTerm, viewLevel, savoirEtudiés, descripteurEtudiés, themeEtudiés]);

  const isEleveGAR = eleve?.synchronise_gar && eleve?.eleve_gar;
  const displayNom = isEleveGAR ? eleve.eleve_gar_nom : eleve?.nom;
  const displayPrenom = isEleveGAR ? eleve.eleve_gar_prenom : eleve?.prenom;

  if (filteredEleve) {
    return (
      <div className=" px-4">
        {/* ═══ HEADER gradient + blobs animés (compact — retour intégré) ═══ */}
        {(() => {
          const classLevel = classeNom?.charAt(0);
          const colorSchemes = {
            '6': { gradient: 'from-blue-600 via-cyan-500 to-sky-400', blob1: '#67e8f9', blob2: '#a5f3fc' },
            '5': { gradient: 'from-purple-600 via-violet-500 to-purple-400', blob1: '#c084fc', blob2: '#e9d5ff' },
            '4': { gradient: 'from-emerald-600 via-green-500 to-emerald-400', blob1: '#34d399', blob2: '#a7f3d0' },
            '3': { gradient: 'from-rose-600 via-pink-500 to-rose-400', blob1: '#fb7185', blob2: '#fecdd3' },
          };
          const colors = colorSchemes[classLevel] || colorSchemes['6'];

          return (
            <div className="relative overflow-hidden rounded-t-2xl">
              {/* Glow */}
              <div className={`absolute -inset-2 bg-gradient-to-r ${colors.gradient} rounded-3xl blur-2xl opacity-30 animate-pulse`} />

              <div className="relative overflow-hidden">
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient}`} />

                {/* Animated liquid blobs */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="hdr-blob1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={colors.blob1} stopOpacity="0.5" />
                      <stop offset="100%" stopColor={colors.blob2} stopOpacity="0.2" />
                    </linearGradient>
                    <linearGradient id="hdr-blob2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={colors.blob2} stopOpacity="0.4" />
                      <stop offset="100%" stopColor={colors.blob1} stopOpacity="0.1" />
                    </linearGradient>
                  </defs>
                  <path fill="url(#hdr-blob1)">
                    <animate attributeName="d" dur="8s" repeatCount="indefinite"
                      values="M0,100 Q200,40 400,80 Q600,130 800,70 L800,200 L0,200 Z;
                              M0,70 Q200,110 400,50 Q600,80 800,120 L800,200 L0,200 Z;
                              M0,100 Q200,40 400,80 Q600,130 800,70 L800,200 L0,200 Z" />
                  </path>
                  <path fill="url(#hdr-blob2)">
                    <animate attributeName="d" dur="6s" repeatCount="indefinite"
                      values="M0,130 Q300,80 500,110 Q700,140 800,90 L800,200 L0,200 Z;
                              M0,90 Q300,130 500,80 Q700,100 800,140 L800,200 L0,200 Z;
                              M0,130 Q300,80 500,110 Q700,140 800,90 L800,200 L0,200 Z" />
                  </path>
                  <circle cx="150" cy="80" r="3" fill="white" opacity="0.4">
                    <animate attributeName="cy" dur="3s" values="80;50;80" repeatCount="indefinite" />
                  </circle>
                  <circle cx="650" cy="100" r="2.5" fill="white" opacity="0.3">
                    <animate attributeName="cy" dur="4s" values="100;60;100" repeatCount="indefinite" />
                  </circle>
                  <circle cx="400" cy="70" r="3" fill="white" opacity="0.35">
                    <animate attributeName="cy" dur="3.5s" values="70;40;70" repeatCount="indefinite" />
                  </circle>
                </svg>

                {/* Contenu — compact : retour + avatar + nom à gauche, stats à droite (1 rangée) */}
                <div className="relative px-3 sm:px-5 py-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    {/* Gauche : retour + avatar + nom */}
                    <div className="flex items-center gap-2.5 min-w-0">
                      <button
                        onClick={() => handleRowClick()}
                        title="Retour à la liste"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium text-xs shrink-0 transition shadow-sm hover:-translate-y-0.5"
                      >
                        ← Retour
                      </button>
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 backdrop-blur rounded-xl border border-white/30
                        flex items-center justify-center text-white font-bold text-sm shadow-lg shrink-0">
                        {displayNom?.[0]}{displayPrenom?.[0]}
                      </div>
                      <div className="min-w-0">
                        <h1 className="text-base sm:text-lg font-extrabold text-white tracking-tight leading-tight truncate">
                          {displayPrenom} {displayNom}
                        </h1>
                        <p className="text-[11px] text-white/60 leading-tight">
                          {eleve?.groupe_nom || eleve?.classe_nom}
                        </p>
                      </div>
                    </div>

                    {/* Droite : stats compactes Moyenne + Classement */}
                    <div className="flex gap-2">
                      <div className="bg-white/15 backdrop-blur-md rounded-lg border border-white/20 px-3 py-1.5 text-center min-w-[66px]">
                        <p className="text-[9px] text-white/80 uppercase tracking-wider font-semibold">Moyenne</p>
                        <p className="text-base sm:text-lg font-extrabold text-white leading-none" style={{ fontFamily: "monospace" }}>
                          {eleveStats?.moyenne > 0 ? eleveStats.moyenne : '-'}
                          <span className="text-[10px] font-medium text-white/40">/20</span>
                        </p>
                      </div>
                      <div className="bg-white/15 backdrop-blur-md rounded-lg border border-white/20 px-3 py-1.5 text-center min-w-[66px]">
                        <p className="text-[9px] text-white/80 uppercase tracking-wider font-semibold">Classement</p>
                        <p className="text-base sm:text-lg font-extrabold text-white leading-none" style={{ fontFamily: "monospace" }}>
                          {eleveStats?.rang || '-'}
                          <span className="text-[10px] font-medium text-white/40">/{listEleves?.length}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══ ONGLETS Acquisition / Historique ═══ */}
              <div className={`relative bg-gradient-to-br ${colors.gradient}`}>
                <div className="absolute inset-0 bg-black/5" />
                <div className="relative flex items-end gap-1 px-4 sm:px-6 pt-1">
                  {[
                    { key: 'acquisition', label: 'Acquisition', icon: '📊' },
                    { key: 'historique', label: 'Historique', icon: '📋' },
                  ].map(({ key, label, icon }) => (
                    <button
                      key={key}
                      onClick={() => handleTypeViewChange(key)}
                      className={`relative px-4 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-wide
                        transition-all duration-200 rounded-t-xl flex items-center gap-1.5 ${
                        typeView === key
                          ? 'bg-white/15 backdrop-blur-md text-white border-b-[2.5px] border-white/60'
                          : 'text-white/40 hover:text-white/70 border-b-[2.5px] border-transparent hover:bg-white/5'
                      }`}
                    >
                      <span>{icon}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                </div>

                {/* Sous-onglets Thèmes / Notions / Savoirs */}
                {typeView === 'acquisition' && (
                  <div className="relative bg-black/8 px-4 sm:px-6 flex items-center gap-0.5 overflow-x-auto">
                    {[
                      { key: 'theme', label: 'Thèmes', icon: '📁', count: themeEtudiés?.length },
                      { key: 'descripteur', label: 'Notions', icon: '📋', count: descripteurEtudiés?.length },
                      { key: 'savoir', label: 'Savoirs', icon: '🎯', count: savoirEtudiés?.length },
                    ].map(({ key, label, icon, count }) => (
                      <button
                        key={key}
                        onClick={() => setViewLevel(key)}
                        className={`lvl-btn relative px-3 sm:px-5 py-2 sm:py-2.5 text-[11px] sm:text-xs font-bold
                          transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 ${
                          viewLevel === key
                            ? 'lvl-active text-white bg-white/8'
                            : 'text-white/45 hover:text-white/70'
                        }`}
                      >
                        <span>{icon}</span>
                        <span>{label}</span>
                        {count > 0 && <span className="text-[10px] opacity-60">({count})</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* CSS pour l'animation underline des sous-onglets */}
        <style>{`
          .lvl-btn { position: relative; cursor: pointer; }
          .lvl-btn::after {
            content: ''; position: absolute; bottom: 0; left: 50%; width: 0; height: 2.5px;
            background: rgba(255,255,255,0.7); border-radius: 2px;
            transition: all 0.2s ease; transform: translateX(-50%);
          }
          .lvl-active::after { width: 70%; }
        `}</style>

        {/* Contenu */}
        <div className="flex flex-col  w-full">
          {(typeView === "historique" && Object.values(filteredEleve).length > 0) &&
            <PassifEleve eleve={filteredEleve} />
          }

          {typeView === "acquisition" &&
            <AcquisitionEleve
              savoirEtudiés={filteredItems}
              viewLevel={viewLevel}
              niveauClasse={niveauDeLaClasse(classeNom || groupNom, [filteredEleve])}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          }
        </div>
      </div>
    )
  }

  return null;
}

export default DetailEleve